<template>
  <div class="main-wrapper">
    <div class="login-page">
      <div class="login-body container">
        <div class="loginbox" style="padding-bottom: 0px">
          <div class="login-right-wrap">
            <div class="account-header">
              <div class="account-logo text-center mb-4">
                <router-link to="/">
                  <a href="/" class="text-lg" style="font-size: 26px">
                    <span
                      class="font-weight-600 text-slate-300"
                      :style="logoPrefixStyle"
                    >
                      {{ branding.logo.prefix }}
                    </span>
                    <span
                      class="font-weight-bold text-slate-500"
                      :style="logoSuffixStyle"
                    >
                      {{ branding.logo.suffix }}
                    </span>
                  </a>
                </router-link>
              </div>
            </div>
            <div class="login-header text-center">
              <h6 class="text-muted">Enter Invitation Code</h6>
              <p class="text-muted mt-2">
                Enter the 6-digit code you received
              </p>
              <div class="mt-4">
                <div
                  v-if="success === true && message"
                  class="alert alert-success"
                  role="alert"
                >
                  <strong>Success!</strong> {{ message }}
                </div>
                <div
                  v-if="success === false && message"
                  class="alert alert-danger"
                  role="alert"
                >
                  {{ message }}
                </div>
              </div>
            </div>

            <form class="vld-parent" ref="formContainer">
              <!-- OTP Input -->
              <div class="form-group">
                <div class="otp-input-container">
                  <input
                    v-for="(digit, index) in otpDigits"
                    :key="index"
                    type="text"
                    maxlength="1"
                    class="otp-input"
                    :ref="'otpInput' + index"
                    v-model="otpDigits[index]"
                    @input="handleOtpInput(index, $event)"
                    @keydown="handleOtpKeydown(index, $event)"
                    @paste="handlePaste($event)"
                    inputmode="numeric"
                    pattern="[0-9]*"
                  />
                </div>
              </div>

              <div class="text-center mt-4">
                <button
                  class="btn btn-primary btn-block account-btn"
                  type="button"
                  @click="verifyCode()"
                  :style="{
                    backgroundColor: branding.buttonColor,
                    color: branding.buttonTextColor,
                    borderColor: branding.buttonColor
                  }"
                  :disabled="loading || !isCodeComplete"
                >
                  <span v-if="!loading">Verify Code</span>
                  <span v-else>
                    <i
                      class="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></i>
                  </span>
                </button>
                <button
                  class="btn btn-primary btn-block account-btn mt-2"
                  type="button"
                  @click="goToLogin()"
                  :style="{
                    backgroundColor: 'white',
                    color: branding.buttonColor,
                    borderColor: branding.buttonColor
                  }"
                >
                  Back to Login
                </button>
              </div>
            </form>

            <div
              class="text-center dont-have mb-4"
              style="margin-top: 1rem !important;"
            >
              <div class="mt-3">
                <small id="terms-footnote" class="footnote">
                  {{ branding.footerText }}
                  <a
                    :href="branding.privacyPolicyUrl"
                    target="_blank"
                    :style="{ color: branding.primaryColor }"
                  >
                    privacy policy
                  </a>
                  and
                  <a
                    :href="branding.termsUrl"
                    target="_blank"
                    :style="{ color: branding.primaryColor }"
                  >
                    terms and conditions
                  </a>
                  that govern your usage of our services.
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getBrandingByDomain } from "@/config/domainBranding";
import authServiceApi from "../../service/AuthServiceApi";

export default {
  name: "JoinBusiness",
  data() {
    return {
      success: null,
      message: "",
      otpDigits: ["", "", "", "", "", ""],
      loading: false,
      branding: getBrandingByDomain(location.host)
    };
  },
  computed: {
    isCodeComplete() {
      return this.otpDigits.every(digit => digit !== "");
    },
    invitationCode() {
      return this.otpDigits.join("");
    },
    logoSuffixStyle() {
      return {
        color: this.branding.logo.suffixColor || "#ffffff",
        fontWeight: "bold",
        backgroundColor:
          this.branding.logo.suffixBackgroundColor ||
          this.branding.primaryColor,
        padding: "6px",
        borderRadius: "5px"
      };
    },
    logoPrefixStyle() {
      return {
        color: this.branding.logo.prefixColor || this.branding.secondaryColor
      };
    }
  },
  methods: {
    handleOtpInput(index, event) {
      const value = event.target.value;

      // Only allow numeric input
      if (!/^\d*$/.test(value)) {
        this.otpDigits[index] = "";
        return;
      }

      // Move to next input if current is filled
      if (value && index < 5) {
        this.$refs["otpInput" + (index + 1)][0].focus();
      }
    },
    handleOtpKeydown(index, event) {
      // Handle backspace
      if (event.key === "Backspace" && !this.otpDigits[index] && index > 0) {
        this.$refs["otpInput" + (index - 1)][0].focus();
      }
    },
    handlePaste(event) {
      event.preventDefault();
      const pastedData = event.clipboardData
        .getData("text")
        .replace(/\D/g, "")
        .slice(0, 6);

      for (let i = 0; i < pastedData.length; i++) {
        this.otpDigits[i] = pastedData[i];
      }

      // Focus last filled input or first empty
      const lastIndex = Math.min(pastedData.length - 1, 5);
      if (this.$refs["otpInput" + lastIndex]) {
        this.$refs["otpInput" + lastIndex][0].focus();
      }
    },
    goToLogin() {
      this.$router.push("/login");
    },
    async verifyCode() {
      if (!this.isCodeComplete) {
        this.success = false;
        this.message = "Please enter the complete 6-digit code";
        return;
      }

      this.loading = true;
      this.success = null;
      this.message = "";

      try {
        const response = await authServiceApi.validateInvitationCode(
          this.invitationCode
        );

        if (response.data.code === "SUCCESS" || response.data.success) {
          const data = response.data.data;

          if (!data.valid) {
            this.handleInvalidInvitation(data);
            return;
          }

          // Store invitation data for the next step
          sessionStorage.setItem("invitationData", JSON.stringify(data));

          // Handle based on nextAction
          switch (data.nextAction) {
            case "REGISTER":
              // User doesn't exist - go to registration
              this.$router.push({
                name: "invitation-register",
                query: { invitationId: data.invitationId }
              });
              break;
            case "SET_PASSWORD":
              // User exists but no password - go to set password
              this.$router.push({
                name: "invitation-set-password",
                query: { invitationId: data.invitationId }
              });
              break;
            case "LOGIN":
              // User exists with password - go to success screen
              sessionStorage.removeItem("invitationData");
              this.$router.push({
                name: "invitation-success",
                query: {
                  businessName: data.businessName || "",
                  email: data.email || "",
                  isExistingUser: "true"
                }
              });
              break;
            default:
              this.handleInvalidInvitation(data);
          }
        } else {
          this.success = false;
          this.message =
            response.data.description ||
            response.data.narrative ||
            "Invalid invitation code";
        }
      } catch (e) {
        console.error("Error validating invitation:", e);
        this.success = false;
        if (e.response && e.response.data) {
          this.message =
            e.response.data.narrative ||
            e.response.data.description ||
            "Failed to validate invitation code";
        } else {
          this.message = "Something went wrong. Please try again.";
        }
      } finally {
        this.loading = false;
      }
    },
    handleInvalidInvitation(data) {
      switch (data.nextAction) {
        case "EXPIRED":
          this.success = false;
          this.message =
            "This invitation has expired. Please request a new one.";
          break;
        case "REVOKED":
          this.success = false;
          this.message = "This invitation has been revoked.";
          break;
        case "ALREADY_ACCEPTED":
          this.success = false;
          this.message = "This invitation has already been accepted.";
          break;
        case "INVALID_TOKEN":
          this.success = false;
          this.message = "Invalid invitation code. Please check and try again.";
          break;
        default:
          this.success = false;
          this.message = data.message || "Invalid invitation code";
      }
    }
  },
  mounted() {
    // Focus first input on mount
    if (this.$refs["otpInput0"]) {
      this.$refs["otpInput0"][0].focus();
    }
  }
};
</script>

<style scoped>
.otp-input-container {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.otp-input {
  width: 50px;
  height: 55px;
  text-align: center;
  font-size: 24px;
  font-weight: bold;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  outline: none;
  transition: border-color 0.2s;
}

.otp-input:focus {
  border-color: #3498db;
}

.spinner-border {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  vertical-align: text-bottom;
  border: 0.1em solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spinner-border 0.75s linear infinite;
}

@keyframes spinner-border {
  to {
    transform: rotate(360deg);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/auth/JoinBusiness.vue